import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useExam } from '../hooks/useExam';
import { GRUPA_PO_SLUGU, LITERY, PATENT_PO_SLUGU, kluczeOdpowiedzi } from '../data/patenty';
import { dzialZPamieci, pobierzDzial } from '../data/pytania';
import { odmien } from '../lib/odmiana';
import { podstawaPrawna } from '../lib/podstawa';
import MarkdownRenderer from './MarkdownRenderer';
import JsonLd from '../seo/JsonLd';
import { absoluteUrl, breadcrumbJsonLd } from '../seo/siteMeta';
import styles from '../css/modules/Nauka.module.css';

/**
 * Tryb nauki: pytania z jednego działu, z natychmiastową informacją zwrotną.
 *
 * W odróżnieniu od symulatora egzaminu poprawna odpowiedź, wyjaśnienie i
 * podstawa prawna pokazują się od razu po kliknięciu - tu chodzi o naukę,
 * a nie o sprawdzenie się w warunkach egzaminacyjnych.
 */
const StudyMode = () => {
  const { patent: patentSlug, grupa: grupaSlug } = useParams();
  const patent = PATENT_PO_SLUGU[patentSlug];
  const grupa = GRUPA_PO_SLUGU[grupaSlug];

  const { questions, error, loading, demo } = useExam({ patent: patentSlug, grupa: grupaSlug });

  const [indeks, setIndeks] = useState(0);
  const [wybrana, setWybrana] = useState(null);
  const [dobrze, setDobrze] = useState(0);
  const [spis, setSpis] = useState(() => dzialZPamieci(grupaSlug, patentSlug) || []);
  const kartaRef = useRef(null);
  /* Pierwsze wyświetlenie nie ma przewijać strony - użytkownik dopiero co
     wszedł i czyta od góry. Przewijamy dopiero przy KOLEJNYM pytaniu. */
  const przewinDoPytania = useRef(false);

  useEffect(() => {
    setIndeks(0);
    setWybrana(null);
    setDobrze(0);
    przewinDoPytania.current = false;
  }, [patentSlug, grupaSlug]);

  /* Zgłoszenie użytkownika: "strona przewija się cały czas do dołu, kiedy
     próbuję rozwiązywać pytania". Przyczyna: przycisk "Następne pytanie" stał
     pod modułem ćwiczenia, czyli poza pierwszym ekranem telefonu. Żeby go
     kliknąć, trzeba było zjechać na dół; nowe pytanie renderowało się z powrotem
     na górze karty, a widok zostawał na spisie pytań. Każde pytanie spychało
     więc użytkownika coraz niżej. Przycisk wrócił do karty (niżej w JSX),
     a tu pilnujemy, żeby po zmianie pytania karta była na ekranie. */
  useEffect(() => {
    if (!przewinDoPytania.current || !kartaRef.current) return;
    przewinDoPytania.current = false;
    const gora = kartaRef.current.getBoundingClientRect().top;
    if (gora < 0 || gora > window.innerHeight * 0.5) {
      kartaRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }, [indeks]);

  /* Spis pytań działu idzie z lokalnych plików (src/data/pytania), a nie z API.
     Dwa powody: prerender nie czeka na odpowiedź obcego hosta, a te linki są
     jedyną drogą, którą robot dochodzi do stron pojedynczych pytań. */
  useEffect(() => {
    let anulowane = false;
    if (!grupaSlug) return undefined;

    const zPamieci = dzialZPamieci(grupaSlug, patentSlug);
    if (zPamieci) {
      setSpis(zPamieci);
      return undefined;
    }

    pobierzDzial(grupaSlug, patentSlug)
      .then(lista => { if (!anulowane) setSpis(lista); })
      .catch(() => { if (!anulowane) setSpis([]); });

    return () => { anulowane = true; };
  }, [patentSlug, grupaSlug]);

  if (!patent || !grupa) {
    return (
      <div className={styles.wrap}>
        <h1 className={styles.h1}>Nie znamy takiego działu</h1>
        <Link className={styles.cta} to="/">Wróć na stronę główną</Link>
      </div>
    );
  }

  /* Ćwiczenie potrzebuje API, spis pytań nie. Kiedyś brak odpowiedzi z API
     zwijał CAŁĄ stronę do komunikatu o błędzie - i tak właśnie 20 adresów
     /nauka trafiło do sitemapy z treścią "Failed to fetch" zamiast zawartości
     (prerender chodzi po localhoście, którego CORS w API nie przepuszczał).
     Teraz brak pytań gasi tylko sam moduł ćwiczenia. */
  const cwiczenieGotowe = !loading && !error && questions.length > 0;
  const pytanie = cwiczenieGotowe ? questions[indeks] : null;
  const klucze = pytanie ? kluczeOdpowiedzi(pytanie) : [];
  const sprawdzone = wybrana !== null;
  const koniec = indeks >= questions.length - 1;

  const zaznacz = key => {
    if (sprawdzone || !pytanie) return;
    setWybrana(key);
    if (key === pytanie.correctAnswer) setDobrze(d => d + 1);
  };

  const dalej = () => {
    setWybrana(null);
    przewinDoPytania.current = true;
    setIndeks(i => Math.min(questions.length - 1, i + 1));
  };

  return (
    <div className={styles.wrap}>
      <JsonLd data={breadcrumbJsonLd([
        { name: 'Strona główna', path: '/' },
        { name: patent.nazwa, path: `/${patent.slug}` },
        { name: 'Nauka', path: `/nauka/${patent.slug}` },
        { name: grupa.nazwa, path: `/nauka/${patent.slug}/${grupa.slug}` },
      ])} />

      <nav className={styles.breadcrumbs}>
        <Link to="/">Strona główna</Link> / <Link to={`/${patent.slug}`}>{patent.nazwa}</Link>
        {' / '}<Link to={`/nauka/${patent.slug}`}>Nauka</Link> / {grupa.nazwa}
      </nav>

      <h1 className={styles.h1}>{grupa.nazwa} - {patent.nazwa}</h1>
      <p className={styles.lead}>{grupa.opis}</p>

      {/* Stała wysokość modułu ćwiczenia: pytania do niego idą z API, więc
          pojawiają się chwilę po podmianie prerenderowanego HTML-u. Bez
          zarezerwowanego miejsca spis pytań i stopka skakały w górę i z powrotem. */}
      <div className={styles.cwiczenie}>
      {cwiczenieGotowe && (
        <div className={styles.postep}>
          <span>Pytanie {indeks + 1} z {questions.length}</span>
          <span>Poprawne: {dobrze}</span>
          {demo && <span className={styles.demoTag}>wersja demo</span>}
        </div>
      )}

      {loading && <p className={styles.info}>Wczytujemy pytania do ćwiczenia…</p>}

      {!loading && !cwiczenieGotowe && (
        <p className={styles.info}>
          {error
            ? 'Ćwiczenie jest chwilowo niedostępne. Wszystkie pytania z tego działu znajdziesz w spisie poniżej.'
            : 'Dla tego patentu nie mamy jeszcze pytań w tym dziale.'}
        </p>
      )}

      {cwiczenieGotowe && (
        <div className={styles.karta} ref={kartaRef}>
          <h2 className={styles.pytanie}>{pytanie.question}</h2>

          <div className={styles.odpowiedzi}>
            {klucze.map(k => {
              const kl = [styles.odpowiedz];
              if (sprawdzone && k === pytanie.correctAnswer) kl.push(styles.dobra);
              else if (sprawdzone && k === wybrana) kl.push(styles.zla);
              else if (k === wybrana) kl.push(styles.wybrana);
              return (
                <button key={k} type="button" className={kl.join(' ')} onClick={() => zaznacz(k)} disabled={sprawdzone}>
                  <span className={styles.litera}>{LITERY[k]}</span>
                  <span>{pytanie[k]}</span>
                </button>
              );
            })}
          </div>

          {sprawdzone && (
            <div className={styles.omowienie}>
              <p className={wybrana === pytanie.correctAnswer ? styles.werdyktOk : styles.werdyktZle}>
                {wybrana === pytanie.correctAnswer ? 'Dobrze' : `Poprawna odpowiedź: ${LITERY[pytanie.correctAnswer]}`}
              </p>
              {pytanie.explanation && <MarkdownRenderer content={pytanie.explanation} />}
              {podstawaPrawna(pytanie) && (
                <p className={styles.podstawa}>Podstawa prawna: {podstawaPrawna(pytanie)}</p>
              )}
              <Link className={styles.link} to={`/pytania/${pytanie.slug}`}>Otwórz osobną stronę tego pytania</Link>
            </div>
          )}

          {/* Przejście dalej jest w karcie, tuż pod omówieniem - tam, gdzie
              użytkownik właśnie patrzy. Wcześniej stało pod całym modułem. */}
          {sprawdzone && !koniec && (
            <button type="button" className={styles.ctaWKarcie} onClick={dalej}>
              Następne pytanie
            </button>
          )}
        </div>
      )}

      </div>

      <div className={styles.akcje}>
        {cwiczenieGotowe && sprawdzone && koniec && (
          <>
            <p className={styles.info}>
              To ostatnie pytanie z tej porcji. Poprawnie: {dobrze} z {questions.length}{' '}
              {odmien(questions.length, ['pytania', 'pytań', 'pytań'])}.
            </p>
            <button type="button" className={styles.cta} onClick={() => window.location.reload()}>
              Losuj nową porcję
            </button>
          </>
        )}
        <Link className={styles.ctaDrugi} to={`/nauka/${patent.slug}`}>Inne działy</Link>
      </div>

      {demo && cwiczenieGotowe && (
        <p className={styles.demoInfo}>
          W wersji demo losujemy niewielką próbkę pytań.{' '}
          <Link to="/cennik">Pełny dostęp</Link> otwiera całą bazę i egzamin próbny na pełne{' '}
          75 pytań.
        </p>
      )}

      {spis.length > 0 && (
        <section className={styles.spis}>
          <JsonLd data={{
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: `Pytania egzaminacyjne: ${grupa.nazwa} - ${patent.nazwa}`,
            numberOfItems: spis.length,
            itemListElement: spis.map((p, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              name: p.question,
              url: absoluteUrl(`/pytania/${p.slug}`),
            })),
          }} />

          <h2 className={styles.h2}>
            Wszystkie pytania z działu: {grupa.nazwa}
          </h2>
          <p className={styles.spisLead}>
            {spis.length} {odmien(spis.length, ['pytanie', 'pytania', 'pytań'])} z bazy egzaminacyjnej
            na patent {patent.dopelniacz}. Każde ma osobną stronę z poprawną odpowiedzią,
            wyjaśnieniem i podstawą prawną.
          </p>
          <ul className={styles.spisLista}>
            {spis.map(p => (
              <li key={p.slug}>
                <Link className={styles.spisLink} to={`/pytania/${p.slug}`}>{p.question}</Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export default StudyMode;
